import { ComponentProps } from 'lib/component-props';
import { Resources } from 'lib/templates/Feature.BOKF.model';
import { Field, Item, ImageField } from '@sitecore-jss/sitecore-jss-nextjs';
import { Bok } from 'lib/templates/Foundation.BOKF.model';
import { tv, VariantProps } from 'tailwind-variants';
import { FilterAnyOf, FilterNot, FilterEqual } from '@sitecore-search/react';

export type IconProps = {
  fields?: { Value?: Field<string> };
};

export type ElementProps = Item & {
  fields?: { Name?: Field<string>; Icon?: IconProps };
};

export type CommonArticleProp = {
  id?: string;
  url?: string;
  fields?: {
    ReadTime?: Field<string>;
    Teaser?: Field<string>;
    Hero?: ImageField;
    Headline?: Field<string>;
    PublishDate?: Field<string>;
    ContentType?: FilterType;
    VideoUrl?: Field<string>;
    Topics?: Array<ElementProps>;
  };
};

export type ResourceItemProps = {
  ribbonLabel?: Field<string>;
  article_time?: string;
  description?: string;
  id?: string;
  image_url?: string;
  media_url?: string;
  name?: string;
  publish_date?: string;
  url?: string;
  type?: string;
  topics: string[];
};

export type FilterType = Item & {
  fields?: {
    PageTitle?: Field<string>;
    Name?: Field<string>;
    ExpertFirstName?: Field<string>;
    ExpertLastName?: Field<string>;
    AuthorName?: Field<string>;
  };
};

export type ResourceLibraryProps = Omit<ComponentProps, 'params'> &
  Resources.Configuration.ResourceLibraryConfiguration & {
    params?: {
      HeadingType?: Bok.BaseTypes.HtmlTag['fields'];
      Alignment?: { Value: Field<string> };
      BackgroundColor?: { Value: Field<string> };
      Spacing?: { Value: Field<string> };
    };
    fields?: {
      ContentType?: Array<ElementProps>;
      Topics?: Array<ElementProps>;
    };
  };

export const ResourceLibraryStyle = tv({
  slots: {
    HeadingStyle: ['relative pb-1 mb-3'],
    checkBokStyle: [
      'relative flex items-center cursor-pointer gap-2 text-light-fill-primary-variant font-normal',
      'before:bg-transparent before:border-2 before:border-light-stroke-primary-variant before:inline-block before:relative before:cursor-pointer',
      'peer-checked:before:bg-light-background-primary peer-checked:before:border-light-stroke-primary-variant',
      'peer-checked:peer-focus:before:bg-light-background-primary-highest peer-focus:before:border-light-stroke-primary-variant-highest',
      'peer-checked:peer-hover:before:bg-light-background-primary-high peer-hover:before:border-light-stroke-primary-variant-high',
      'peer-checked:after:block peer-checked:after:absolute md:peer-checked:after:top-[5px] md:peer-checked:after:left-[9px] md:peer-checked:after:w-[6px] md:peer-checked:after:h-[14px] peer-checked:after:border-light-stroke-inverse peer-checked:after:border-r-2 peer-checked:after:border-b-2 peer-checked:after:rotate-45 peer-checked:font-medium peer-checked:border-light-stroke-primary-highest',
      'hover:before:border-light-stroke-primary-variant-high focus:before:border-light-stroke-primary-variant-highest peer-checked:text-light-fill-primary',
      'text-sm md:p2 before:p-2 md:before:p-[10px] peer-checked:after:top-[3px] peer-checked:after:left-2 peer-checked:after:w-[4px] peer-checked:after:h-[11px]',
    ],
  },
  variants: {
    positions: {
      center: {
        HeadingStyle: ['after:left-1/2 after:-translate-x-1/2 text-center'],
      },
      left: {
        HeadingStyle: ['after:left-0 text-left'],
      },
    },
    tagStyle: {
      'title-border title-border--thick title-border--neutral': {
        HeadingStyle: [
          'after:relative after:block after:border-b-[5px] after:border-light-stroke-secondary-brand after:w-12 after:mb-3 after:pt-1 md:after:pt-2 lg:after:pt-4 lg:after:mb-2',
        ],
      },
      'title-border': {
        HeadingStyle: [
          'after:relative after:block after:border-b-[4px] after:border-light-stroke-secondary-brand after:w-10 after:mb-3 after:pt-2 lg:after:pt-3 lg:after:mb-2',
        ],
      },
      default: {
        HeadingStyle: '',
      },
    },
  },
});

export type variantsTypes = VariantProps<typeof ResourceLibraryStyle>;

// Helper function to check and transform resource type
export const getResourceType = (type: string): string => {
  if (type.toLowerCase() === 'video') {
    return 'VideoResource';
  } else if (type.toLowerCase() === 'calculator') {
    return 'CalculatorResource';
  }
  return type;
};

export const getResourceFilterType = (type: string): string => {
  const resourceType = type.toLowerCase();
  if (resourceType === 'videoresource') {
    return 'video';
  }
  if (resourceType === 'calculatorresource') {
    return 'calculator';
  }

  return resourceType;
};

// Helper function to map field values from filter types
export const mapFieldValues = (
  field: keyof FilterType['fields'],
  items: FilterType[] = []
): string[] =>
  items.map((item) => {
    const fieldValue = item.fields?.[field];

    // Check if fieldValue is of type Field<GenericFieldValue> before accessing 'value'
    if (fieldValue && typeof fieldValue === 'object' && 'value' in fieldValue) {
      return fieldValue.value as string;
    }
    return '';
  });

// Helper function to create base filters from props
export const createBaseFilters = (
  props: ResourceLibraryProps,
  mapFieldValues: (field: keyof FilterType['fields'], items: FilterType[]) => string[]
): (FilterAnyOf | FilterNot)[] => {
  const filters: (FilterAnyOf | FilterNot)[] = [];
  const SearchIDEnvPostFix = process.env.NEXT_PUBLIC_SEARCH_ID_ENV_POSTFIX || '';

  if (props.fields?.Category && props.fields?.Category.length > 0) {
    filters.push(new FilterAnyOf('category', mapFieldValues('PageTitle', props.fields.Category)));
  }

  if (props.fields?.Authors && props.fields?.Authors.length > 0) {
    filters.push(new FilterAnyOf('author', mapFieldValues('AuthorName', props.fields.Authors)));
  }

  if (props.fields?.Experts && props.fields?.Experts.length > 0) {
    const expertNames = props.fields.Experts.map(
      (expert) =>
        `${(expert.fields?.ExpertFirstName as Field<string>)?.value} ${
          (expert.fields?.ExpertLastName as Field<string>)?.value
        }`
    );
    filters.push(new FilterAnyOf('experts', expertNames));
  }

  if (props.fields?.ArticlesToExclude && props.fields?.ArticlesToExclude.length > 0) {
    props.fields.ArticlesToExclude.forEach((exclude) =>
      filters.push(new FilterNot(new FilterEqual('id', exclude.id + SearchIDEnvPostFix)))
    );
  }

  if (props.fields?.ArticlesToInclude && props.fields?.ArticlesToInclude.length > 0) {
    props.fields.ArticlesToInclude.forEach((include) =>
      filters.push(new FilterNot(new FilterEqual('id', include.id + SearchIDEnvPostFix)))
    );
  }

  return filters;
};

// Helper function to create type and topic filters
export const createTypeTopicFilters = (
  fields: ResourceLibraryProps['fields'],
  checkResourceType: (type: string) => string
): {
  filters: (FilterAnyOf | FilterNot)[];
  typesItems: string[];
  topicsItems: string[];
} => {
  const filters: (FilterAnyOf | FilterNot)[] = [];
  let typesItems: string[] = [];
  let topicsItems: string[] = [];

  if (fields?.ContentType && fields?.ContentType.length > 0) {
    typesItems = fields?.ContentType?.map((item: ElementProps) =>
      checkResourceType(item?.fields?.Name?.value as string)
    );
    filters.push(new FilterAnyOf('type', typesItems));
  }

  if (fields?.Topics && fields?.Topics.length > 0) {
    topicsItems = fields?.Topics?.map((item: ElementProps) => item.name);
    filters.push(new FilterAnyOf('topics', topicsItems));
  }

  return { filters, typesItems, topicsItems };
};

// Helper function to transform included resources
export const transformIncludedResources = (
  articles: CommonArticleProp[],
  checkResourceType: (type: string) => string
): ResourceItemProps[] => {
  const includedResources: Array<ResourceItemProps> = [];

  articles?.forEach((resource: CommonArticleProp) => {
    const articleTopics = resource?.fields?.Topics
      ? resource?.fields?.Topics.map((item) => item?.name)
      : [];
    const finalTopics = [...articleTopics.filter((item) => item !== undefined)];

    const includedResource: ResourceItemProps = {
      article_time: resource?.fields?.ReadTime?.value ?? '',
      description: resource?.fields?.Teaser?.value ?? '',
      id: resource?.id ?? '',
      image_url: resource?.fields?.Hero?.value?.src ?? '',
      media_url: resource?.fields?.VideoUrl?.value ?? '',
      name: resource?.fields?.Headline?.value ?? '',
      publish_date: resource?.fields?.PublishDate?.value ?? '',
      url: resource?.url,
      type: checkResourceType(resource?.fields?.ContentType?.fields.Name?.value as string),
      topics: finalTopics,
    };
    includedResources.push(includedResource);
  });

  return includedResources;
};

// Helper function to update query string parameters
export const updateQueryString = (param: string, values: string[]): void => {
  const params = new URLSearchParams(window.location.search);

  if (values.length > 0) {
    params.set(param, values.join(','));
  } else {
    params.delete(param);
  }

  // Manually construct the URL to avoid encoding commas
  const queryString = params.toString().replace(/%2C/g, ',');
  const newUrl = queryString
    ? `${window.location.pathname}?${queryString}`
    : window.location.pathname;
  window.history.pushState({}, '', newUrl);
};

// Helper function to filter included resources
export const filterIncludedResources = (
  includedResources: ResourceItemProps[],
  types: string[],
  topics: string[]
): ResourceItemProps[] => {
  if (types.length === 0 && topics.length === 0) {
    return [];
  }

  return includedResources.filter((resource) => {
    const typeMatch = types.length > 0 ? types.includes(resource.type as string) : true;
    const topicMatch =
      topics.length > 0
        ? resource.topics && resource.topics.some((item) => topics.includes(item))
        : true;

    return typeMatch && topicMatch;
  });
};

// Helper function to build updated content type array
export const buildUpdatedContentType = (
  activeObj: { field: string; value: string; remove: boolean },
  contentType: string[]
): string[] => {
  if (activeObj.remove) {
    return contentType.filter((item) => item !== activeObj.value);
  }

  if (activeObj.field === 'contentType') {
    return [...contentType, activeObj.value];
  }

  return [...contentType];
};

// Helper function to build updated topics array
export const buildUpdatedTopics = (
  activeObj: { field: string; value: string; remove: boolean },
  topics: string[]
): string[] => {
  if (activeObj.remove) {
    return topics.filter((item) => item !== activeObj.value);
  }

  if (activeObj.field === 'topic') {
    return [...topics, activeObj.value];
  }

  return [...topics];
};

// Helper function to get display name for resource type
export const getResourceTypeDisplayName = (type: string): string => {
  const lowerType = type.toLowerCase();

  if (lowerType === 'calculatorresource') {
    return 'Calculator';
  }

  if (lowerType === 'videoresource') {
    return 'Video';
  }

  return type;
};
